import { Fragment } from "react";
import { Link } from "react-router";

import { SkeletonAwait } from "~/components/suspense/skeleton";
import { useURL } from "~/contexts";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "~/hec/components/ui/breadcrumb";
import type { PathRecord } from "~/lib/app.types";
import { cn } from "~/lib/ui";
import { memoizeDerivedPromise } from "~/lib/utils/memoize-derived-promise";
import type { CategoryAltUrlMap, CategorySlugMap } from "~/lib/utils/url";
import { useLayoutData } from "~/routes/layout";

import { Skeleton } from "./ui/skeleton";

const separatorClasses = "text-blue-03 [&>svg]:h-[16px]";

type ResolvedCategoryMaps = {
  slugMap: CategorySlugMap;
  altUrlMap: CategoryAltUrlMap;
};

/**
 * The layout loader streams `categorySlugMap` / `categoryAltUrlMap` (perf,
 * a6c16d68), so on the client they are usually promises. `<Await>` needs ONE
 * stable promise per source pair; memoized by source identity so a layout
 * revalidation derives a new one exactly once and every render in between
 * (fetcher state changes, transitions, suspend retries) reuses it.
 */
const resolveCategoryMaps = memoizeDerivedPromise(
  (
    slug: CategorySlugMap | Promise<CategorySlugMap>,
    alt: CategoryAltUrlMap | Promise<CategoryAltUrlMap>,
  ): ResolvedCategoryMaps | Promise<ResolvedCategoryMaps> =>
    slug instanceof Promise || alt instanceof Promise
      ? Promise.all([slug, alt]).then(([slugMap, altUrlMap]) => ({
          slugMap,
          altUrlMap,
        }))
      : { slugMap: slug, altUrlMap: alt },
);

export const BreadcrumbsSkeleton = ({ size = 2 }) => {
  return (
    <div className="mb-4 flex flex-row border-b-2 border-[#f5f5f5] pb-2.5">
      <Skeleton className="h-5.5 w-8" />
      {Array.from({ length: size }, (_, i) => (
        <Fragment key={i}>
          <div className="w-2"></div>
          <Skeleton className="h-5.5 w-20" />
        </Fragment>
      ))}
    </div>
  );
};

const Breadcrumbs = ({
  categories,
  pageName,
  isSearchResult,
  searchQuery,
  showHome = true,
  mobileBackLink = false,
}: {
  categories?: PathRecord[];
  pageName?: string;
  isSearchResult?: boolean;
  searchQuery?: string;
  showHome?: boolean;
  /**
   * PDP parity: under 992px collapse the trail to a single `‹ {parent}`
   * back-link (immediate parent category); the full trail shows at md+.
   * CSS-toggled so SSR and client render the same markup (no hydration shift).
   */
  mobileBackLink?: boolean;
}) => {
  const url = useURL();
  const { categorySlugMap, categoryAltUrlMap } = useLayoutData();
  // COM-405 / COM-406: MUST be referentially stable across renders — see
  // `resolveCategoryMaps`. Deriving the promise inline here put every router
  // state update into an infinite suspend/retry loop once the layout streamed
  // these maps as promises (PLP dead after the add-to-cart modal).
  const maps = resolveCategoryMaps(categorySlugMap, categoryAltUrlMap);

  return (
    <SkeletonAwait resolve={maps} skeleton={<BreadcrumbsSkeleton />}>
      {({ slugMap, altUrlMap }) => {
        // Deepest category = immediate parent of the product; build its full slug
        // path the same way the trail does (concatenate each ancestor slug).
        const parent =
          mobileBackLink && !isSearchResult
            ? categories?.[categories.length - 1]
            : undefined;
        let parentLink = "";
        if (parent?.href) {
          // Explicit target — see PathRecord.href. Never set by the PDP, the only
          // caller that uses mobileBackLink.
          parentLink = parent.href;
        } else if (parent && categories) {
          // SFCC-11632: if the immediate parent has an all-state alternativeUrl, link
          // straight to it; otherwise build its cumulative slug path.
          const parentAltUrl = altUrlMap[parent.id ?? ""];
          if (parentAltUrl) {
            parentLink = parentAltUrl;
          } else {
            for (let i = 0; i < categories.length; i++) {
              const id = categories[i]?.id ?? "";
              const slug = id ? (slugMap[id] ?? id) : "";
              parentLink += slug ? `/${slug}` : "";
            }
          }
        }

        return (
          <>
            {parent && (
              <div className="min-[992px]:hidden mb-4 flex items-center border-b-2 border-[#f5f5f5] pb-2.5">
                <Link
                  to={url(parentLink)}
                  prefetch="intent"
                  data-testid="breadcrumb-back"
                  className="inline-flex items-center gap-1 font-grotesque text-sm font-light text-blue-03 underline hover:no-underline"
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="16"
                    height="16"
                    stroke="currentColor"
                    strokeWidth="2"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                  {parent.name}
                </Link>
              </div>
            )}
            <Breadcrumb
              data-testid={undefined}
              className={cn(
                "no-scrollbar mb-4 w-full overflow-x-auto border-b-2 border-[#f5f5f5] pb-2.5",
                mobileBackLink && "min-[992px]:block hidden",
              )}
            >
              <BreadcrumbList
                data-testid={undefined}
                className="font-grotesque text-sm font-light leading-5.5 text-blue-03"
              >
                {showHome && (
                  <BreadcrumbItem data-testid={undefined}>
                    <Link
                      to={url("/")}
                      prefetch="intent"
                      data-testid="breadcrumb-home"
                      className="font-light text-blue-03 hover:underline"
                    >
                      Home
                    </Link>
                  </BreadcrumbItem>
                )}

                {isSearchResult ? (
                  <>
                    {showHome && (
                      <BreadcrumbSeparator
                        data-testid={undefined}
                        className={separatorClasses}
                      />
                    )}
                    <BreadcrumbItem data-testid={undefined}>
                      <BreadcrumbPage
                        data-testid="breadcrumb-current"
                        className="font-light text-blue-04"
                      >
                        Your search results for &quot;{searchQuery ?? ""}&quot;
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                ) : (
                  categories?.map((category, i) => {
                    // SFCC-10618: drop the `/shop/` prefix. Breadcrumbs build a
                    // multi-segment slug (e.g. `/personal-care/acne-and-skincare`)
                    // by concatenating each category id up to the current index;
                    // the catch-all dispatcher resolves these via getUrlMapping
                    // (or the SCAPI getCategory probe fallback).
                    //
                    // SFCC-10716: rewrite each segment id through the layout's
                    // slug map so categories with `c_alternativeUrl` use their
                    // production URL slug (e.g. `pain-relief-and-recovery`)
                    // instead of the catalog id (`joint-and-muscle-pain-relief`).
                    // SCAPI's `parentCategoryTree` only returns id+name per
                    // ancestor — the slug map is the only source of c_alternativeUrl
                    // for breadcrumb ancestors.
                    // SFCC-11632: an ancestor whose canonical landing is its
                    // all-state subcategory (e.g. Personal Care →
                    // /personal-care/all-personal-care) links to that full
                    // alternativeUrl. Otherwise concatenate the ancestor slug path.
                    // An explicit `href` outranks both: account-area trails aren't
                    // category paths, and the concatenation below would turn
                    // `My Account > Order History` into `/my-account/orders`.
                    const ownAltUrl =
                      category?.href ?? altUrlMap[categories[i]?.id ?? ""];
                    let link = ownAltUrl ?? "";
                    if (!link) {
                      for (let index = 0; index <= i; index++) {
                        const id = categories[index]?.id ?? "";
                        const slug = id ? (slugMap[id] ?? id) : "";
                        link += slug ? `/${slug}` : "";
                      }
                    }
                    const isLast = i === categories.length - 1;
                    const renderAsCurrent = isLast && !pageName;
                    const key = category?.id || category?.name || String(i);

                    const isFirst = i === 0;
                    const renderSeparator = showHome || !isFirst;

                    return (
                      <Fragment key={key}>
                        {renderSeparator && (
                          <BreadcrumbSeparator
                            data-testid={undefined}
                            className={separatorClasses}
                          />
                        )}
                        <BreadcrumbItem data-testid={undefined}>
                          {renderAsCurrent ? (
                            <BreadcrumbPage
                              data-testid="breadcrumb-current"
                              className="font-light text-blue-04"
                            >
                              {category.name}
                            </BreadcrumbPage>
                          ) : (
                            // COM-829: "intent", like the back + Home links —
                            // "viewport" prefetched every visible ancestor's PLP
                            // loader on each desktop PDP/PLP view.
                            <Link
                              to={url(link)}
                              prefetch="intent"
                              data-testid={`breadcrumb-link-${key}`}
                              className="font-light text-blue-03 underline hover:no-underline"
                            >
                              {category.name}
                            </Link>
                          )}
                        </BreadcrumbItem>
                      </Fragment>
                    );
                  })
                )}

                {pageName && !isSearchResult && (
                  <>
                    <BreadcrumbSeparator
                      data-testid={undefined}
                      className={separatorClasses}
                    />
                    <BreadcrumbItem data-testid={undefined}>
                      <BreadcrumbPage
                        data-testid="breadcrumb-current"
                        className="font-light text-blue-04"
                      >
                        {pageName}
                      </BreadcrumbPage>
                    </BreadcrumbItem>
                  </>
                )}
              </BreadcrumbList>
            </Breadcrumb>
          </>
        );
      }}
    </SkeletonAwait>
  );
};

export default Breadcrumbs;
